import CallEndIcon from '@mui/icons-material/CallEnd';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import {Fab, Typography} from '@mui/material';
import {ManagedStream} from '@quobis/sippojs';
import React, {ReactElement, useContext, useEffect, useState} from 'react';

import {QuobisServiceContext, RouterContext} from '../providers/Context.providers';
import envService from '../services/EnvService';

import AudioComponent from './AudioComponent';
import styles from './Call.module.scss';
import {RouteNames} from './RouterContainer';
import VideoComponent from './VideoComponent';

export default function Call(): JSX.Element {
	const hasVideo = envService.HAS_VIDEO;
	const hasMuteAudio = envService.HAS_MUTE_AUDIO;
	const quobisService = useContext(QuobisServiceContext);
	const router = useContext(RouterContext);

	const [localStream, setLocalStream] = useState<ManagedStream>();
	const [remoteStreams, setRemoteStreams] = useState<ManagedStream[]>();
	const [audioStream, setAudioStream] = useState<ManagedStream>();

	const [videoMuted, setVideoMuted] = useState<boolean>(false);
	const [audioMuted, setAudioMuted] = useState<boolean>(false);

	useEffect(() => {
		quobisService?.callService.localStream$.subscribe(
			managedStream => setLocalStream(managedStream),
		);
		quobisService?.callService.remoteStreams$.subscribe(
			managedStreams => setRemoteStreams(managedStreams),
		);
		quobisService?.callService.audioStream$.subscribe(
			managedStreams => setAudioStream(managedStreams),
		);
	}, [quobisService, hasVideo]);

	const leave = async (): Promise<void> => {
		await quobisService?.leave();
		router?.setRoute(RouteNames.DIALPAD);
	};

	const toggleAudio = (): void => {
		if (quobisService) {
			const isMuted = quobisService.toggleAudio();
			setAudioMuted(isMuted);
		}
	};

	const toggleVideo = async (): Promise<void> => {
		if (quobisService) {
			const isMuted = await quobisService.toggleVideo();
			setVideoMuted(isMuted);
		}
	};

	const getLocalElement = (): ReactElement | null => localStream ?
		<VideoComponent managedStream={localStream} />
		:
		null;

	const getRemoteElements = (): ReactElement | null => <div>
		{
			remoteStreams?.map(
				managedStream => <VideoComponent
					managedStream={managedStream}
					key={managedStream.mediaStream.id}
				/>,
			)
		}
	</div>;

	const getAudioElement = (): ReactElement | null => audioStream ?
		<AudioComponent managedStream={audioStream} />
		:
		null;

	const getAudioIcon = (): ReactElement => !audioMuted ? <MicIcon /> : <MicOffIcon />;

	const getVideoIcon = (): ReactElement => !videoMuted ? <VideocamIcon /> : <VideocamOffIcon />;

	return (
		<div className={styles.call}>
			<div className={styles.videoContainer}>
				{ hasVideo && getLocalElement() }
				{ hasVideo && getRemoteElements() }
				{ getAudioElement() }
			</div>

			<Typography variant="h5" margin={4}>Call established</Typography>
			<div className={styles.buttonContainer}>
				{
					hasMuteAudio ?
						<Fab color="primary" aria-label="Mute audio" onClick={toggleAudio} >
							{getAudioIcon()}
						</Fab>
						:
						null
				}
				{
					hasVideo ?
						<Fab color="primary" aria-label="End call" onClick={toggleVideo} >
							{getVideoIcon()}
						</Fab>
						:
						null
				}
				<Fab color="primary" aria-label="End call" onClick={leave} >
					<CallEndIcon />
				</Fab>
			</div>
		</div>
	);
}
